import React, { useState, useRef, DragEvent, ChangeEvent } from "react";
import {
  Box,
  Button,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
  VStack,
  Icon,
  Stack,
} from "@chakra-ui/react";
import { UploadCloud } from "lucide-react";
import { logEvent } from "src/utlis/analytics";
import {
	formatRetryAfter,
	getRateLimitFromRestBody,
} from "src/helpers/rate-limit";

export interface ResumeData {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  yearsOfExperience?: number;
  educationItems: Array<{
    schoolName: string;
    fieldOfStudy?: string;
    qualification: string;
  }>;
  employmentHistoryItems: Array<{
    companyName: string;
    jobTitle: string;
    description: string;
    startedAt: string;
    endedAt?: string;
  }>;
  projectHistoryItems: Array<{
    name: string;
    description: string;
    startedAt?: string;
    endedAt?: string;
  }>;
  certificationItems: Array<{
    name: string;
    issuerCertificationId?: string;
    issuedBy: string;
    issuedAt: string;
    url?: string;
  }>;
}

interface ResumeUploadProps {
  onUploadSuccess: (data: ResumeData) => void;
  jobId?: string;
}

export default function ResumeUpload({ onUploadSuccess, jobId }: ResumeUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isParsed, setIsParsed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const processFile = async (selectedFile: File) => {
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(pdf|doc|docx)$/i)) {
      setError("Please upload a PDF or Word document.");
      setFile(null);
      setIsParsed(false);
      toast({
        title: "Invalid file type",
        description: "Please upload a PDF or Word document.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      setError("Resume must be smaller than 5MB.");
      setFile(null);
      setIsParsed(false);
      toast({
        title: "File too large",
        description: "Resume must be smaller than 5MB.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setError(null);
    setFile(selectedFile);
    setIsParsed(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  const uploadAndParseResume = async () => {
    if (!file) return;

    setIsUploading(true);
    setError(null);

    logEvent("job_cv_extraction_started", {
      file_type: file.type,
      file_size_kb: Math.round(file.size / 1024),
      jobAdId: jobId,
      platform: "pro",
    });

    try {
      const formData = new FormData();
      formData.append("resume", file);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/employer-job-application/parse-resume`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      const rateLimit = getRateLimitFromRestBody(response.status, result);
      if (rateLimit) {
        const description = `You've parsed too many resumes recently. Please try again in ${formatRetryAfter(
          rateLimit.retryAfterSeconds
        )}, or fill the form manually.`;
        setError(description);
        toast({
          title: "Too many resume uploads",
          description,
          status: "warning",
          duration: 7000,
          isClosable: true,
        });
        setFile(null);
        setIsParsed(false);
        return;
      }

      if (!response.ok) {
        throw new Error(result.error?.message || "Failed to parse resume");
      }

      if (result.success && result.data) {
        setIsParsed(true);
        toast({
          title: "Resume parsed successfully",
          description: "We've prefilled your application form with data from your resume.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        onUploadSuccess(result.data);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error: any) {
      console.error("Resume parsing error:", error);
      setError(
        error.message ||
          "An unexpected error occurred. Please try again or fill the form manually."
      );
      toast({
        title: "Could not parse resume",
        description: error.message || "An unexpected error occurred. Please try again or fill the form manually.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
      setFile(null);
      setIsParsed(false);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeFile = () => {
    setFile(null);
    setError(null);
    setIsParsed(false);
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
		<Box mb={8}>
			<Stack spacing={5}>
				<Box
					border="2px dashed"
					borderColor={
						file
							? "green.400"
							: isDragging
								? "flatteredFlamingo.500"
								: "#D7D7D7"
					}
					bg={isDragging ? "flatteredFlamingo.50" : "white"}
					rounded={0}
					p={8}
					textAlign="center"
					cursor={isUploading ? "default" : "pointer"}
					onClick={() => {
						if (!isUploading) {
							fileInputRef.current?.click();
						}
					}}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					transition="border-color 0.2s, background-color 0.2s"
				>
					<input
						type="file"
						ref={fileInputRef}
						onChange={handleFileChange}
						accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
						style={{ display: "none" }}
					/>

					<VStack spacing={3}>
						<Icon
							as={UploadCloud}
							w={10}
							h={10}
							color={file ? "green.500" : "gray.400"}
						/>
						{file ? (
							<>
								<Text
									fontWeight="medium"
									color={isParsed ? "green.600" : "gray.700"}
								>
									{file.name}
								</Text>
								<Text fontSize="sm" color="gray.500">
									{isParsed
										? "Your application form has been prefilled. Click to choose a different file."
										: "Click to choose a different file"}
								</Text>
							</>
						) : (
							<>
								<Text fontWeight="medium" fontSize="lg" color="gray.700">
									Upload your CV
								</Text>
								<Text fontSize="sm" color="gray.500">
									Drag & drop your CV here, or click to browse
								</Text>
								<Text fontSize="xs" color="gray.400">
									PDF or Word document, max 5MB
								</Text>
							</>
						)}
					</VStack>
				</Box>

				{error && (
					<Alert status="error" rounded={0}>
						<AlertIcon />
						<AlertDescription fontSize="sm">{error}</AlertDescription>
					</Alert>
				)}

				<div className="flex items-center justify-center gap-4">
					<Button
						type="button"
						onClick={uploadAndParseResume}
						colorScheme="brand"
						rounded={0}
						px={8}
						py={6}
						isDisabled={!file || isUploading || isParsed}
						isLoading={isUploading}
						loadingText="Reading your CV..."
					>
						{isParsed ? "CV details extracted" : "Extract my CV details"}
					</Button>

					{file && (
						<Button
							colorScheme="prussianBlue"
							variant="outline"
							rounded={0}
							size="lg"
							onClick={removeFile}
							isDisabled={isUploading}
						>
							Remove file
						</Button>
					)}
				</div>
			</Stack>
		</Box>
	);
}
